/* eslint-disable @typescript-eslint/no-empty-interface */
import FollowerRange from '@gp/components/FollowerRange';
import GeneralModal from '@gp/components/GeneralModal';
import GPSelect from '@gp/components/Select';
import {
  CONSTANT_TEXTS,
  FIELD_COMMON_ERR_MSG,
  MILESTONE_CATEGORY_LIST_OPTIONS,
} from '@gp/constants/const';
import { MilestoneDateStatus } from '@gp/enums/milestone';
import TBCDatePicker from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/TBCDatePicker';
import { MilestoneDateTips } from '@gp/pages/Pipeline/ProjectDetails/Milestone/constants/elements';
import type {
  IMilestone,
  IMilestoneBaseFormModel,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import {
  enhanceMilestoneFieldChangeHandler,
  formatMilestoneOptions,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { formatTeaFormItemMessage, formatTeaFormItemStatus } from '@gp/utils/common';
import TipContext from '@home/components/TipContext';
import type { IBaseProps } from '@home/types/common';
import { isEmptyValue } from '@home/utils/common';
import { useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form-hooks';
import { Col, Form, FormItem, Row, SelectOption } from 'tea-component';

import './index.scss';

// 新增表单对象类型
export interface IAddMilestoneFormModel extends IMilestoneBaseFormModel {}

interface IAddMilestoneModalProps extends IBaseProps {
  milestones?: IMilestone[];
  // 弹窗是否可见
  visible?: boolean;
  // 弹窗是否可见的setState
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  // confirm的回调
  onConfirm?: (payload: IAddMilestoneFormModel, isAdd: boolean) => void;
  // cancel的回调
  onCancel?: (payload: IAddMilestoneFormModel) => void;
}

const DEFAULT_FORM_MODEL = {
  milestone: '',
  category: '',
  date: '',
  // is_gr: BoolFlag.False,
  // is_launch: BoolFlag.False,
  date_confirm: MilestoneDateStatus.Confirm,
};

const IGNORE_VALIDATE_KEYS = ['date_confirm', 'is_gr', 'is_launch'];
const handleValidate = (values?: IAddMilestoneFormModel): Record<string, string> => {
  if (!values) return { milestone: FIELD_COMMON_ERR_MSG, date: FIELD_COMMON_ERR_MSG };
  const errors: Record<string, string> = {};
  const entries = Object.entries(values);
  for (const entry of entries) {
    const [key, value] = entry;
    if (IGNORE_VALIDATE_KEYS.includes(key)) continue;
    if (isEmptyValue(value?.trim())) {
      errors[key] = FIELD_COMMON_ERR_MSG;
    }
  }
  return errors;
};

const getIsFormAvailable = (values?: IAddMilestoneFormModel): boolean => {
  const { milestone, date } = values || {};
  return !!milestone || !!date;
};

const AddMilestoneModal: React.FC<IAddMilestoneModalProps> = (props) => {
  const {
    style,
    className,
    milestones,
    visible = false,
    setVisible,
    onConfirm,
    onCancel,
    milestoneCategory,
    milestone,
    isAdd,
  } = props;

  const milestoneOptions = useMemo<SelectOption[]>(
    () => formatMilestoneOptions(milestones),
    [milestones],
  );

  const handleConfirm = useMemoizedFn(async (values: IAddMilestoneFormModel) => {
    await onConfirm?.(values, isAdd);
    setSubmitted(false);
    form?.restart();
  });

  const handleCancel = useMemoizedFn(() => {
    setVisible?.(false);
    setSubmitted(false);
    onCancel?.(values);
    form?.restart();
  });

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });
  const milestoneField = useField('milestone', form);
  const categoryField = useField('category', form);
  const dateField = useField('date', form);
  const dateConfirmField = useField('date_confirm', form);

  // eslint-disable-next-line arrow-body-style
  const onMilestoneChange = useMemo<(payload: string) => void>(() => {
    return enhanceMilestoneFieldChangeHandler(milestoneField, categoryField, milestoneCategory);
  }, [milestoneField, categoryField, milestoneCategory]);

  const [submitted, setSubmitted] = useState(false);
  const formAvailable = useMemo<boolean>(() => getIsFormAvailable(values), [values]);

  const disabledCategory = useMemo(() => {
    const key = values?.milestone || '';
    const hasKey = milestoneCategory ? Object.keys(milestoneCategory).includes(key) : false;
    return hasKey || !key;
  }, [values, milestoneCategory]);

  const handleSubmitInPage = useMemoizedFn(() => {
    setSubmitted(true);
    handleSubmit?.();
  });

  const pristine = useMemo<boolean>(() => {
    const commonsPristine = [milestoneField, dateField, dateConfirmField, categoryField].every(
      (field) => !!field?.meta?.pristine,
    );
    return commonsPristine;
  }, [milestoneField, dateField, dateConfirmField, categoryField]);

  useEffect(() => {
    if (!visible) return;
    const {
      milestone: milestoneName,
      date: milestoneDate,
      date_confirm: dateConfirm,
      category,
    } = milestone || {};
    const initialValues = {
      ...DEFAULT_FORM_MODEL,
      milestone: milestoneName || '',
      category: category || milestoneCategory?.[milestoneName || ''] || '',
      date: milestoneDate || '',
      date_confirm: dateConfirm || MilestoneDateStatus.Confirm,
    };
    form?.initialize(initialValues);
  }, [visible, milestone]);

  return (
    <GeneralModal
      style={style}
      className={classnames('dbg-add-milestone-modal', className)}
      // modalSize="m"
      modalSize="auto"
      title={isAdd ? 'New Production Milestone' : 'Update Time'}
      isFont14={true}
      disabledConfirm={pristine || !formAvailable}
      loadingConfirm={submitting}
      visible={visible}
      onConfirm={handleSubmitInPage}
      onClose={handleCancel}
      content={
        <div className="dbg-add-milestone font14">
          <Form
            className="gp-form dbg-add-milestone-form dbg-add-milestone-form--milestone"
            layout="fixed"
            // fixedLabelWidth={300}
          >
            <Row>
              <Col span={24}>
                <FormItem
                  required
                  label={CONSTANT_TEXTS.MILESTONE}
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, milestoneField?.meta)}
                  message={formatTeaFormItemMessage(submitted, milestoneField?.meta)}
                >
                  <div className="dbg-team-game-information-edit-modal-content-form-Studio">
                    <div className="dbg-team-game-information-edit-modal-content-form-Studio-left">
                      <FollowerRange
                        {...milestoneField?.input}
                        placeholder="Please select Milestone"
                        customizePlaceholder="Please input Milestone"
                        options={milestoneOptions}
                        onChange={onMilestoneChange}
                      />
                    </div>
                    <div className="dbg-update-edit-divider">-</div>
                    <div className="dbg-team-game-information-edit-modal-content-form-Studio-right">
                      <GPSelect
                        {...categoryField?.input}
                        placeholder="Please select Category"
                        clearable={false}
                        options={MILESTONE_CATEGORY_LIST_OPTIONS}
                        disabled={disabledCategory}
                      />
                    </div>
                  </div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem
                  required
                  label={
                    <>
                      {CONSTANT_TEXTS.MILESTONE_TIME}
                      <TipContext
                        className="dbg-gr-edit-tips"
                        placement="right"
                        AWidth={555}
                        tipText={<MilestoneDateTips />}
                      />
                    </>
                  }
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, dateField?.meta)}
                  message={formatTeaFormItemMessage(submitted, dateField?.meta)}
                >
                  <TBCDatePicker
                    value={{
                      dateConfirm: dateConfirmField?.input?.value,
                      date: dateField?.input?.value,
                    }}
                    onChange={(payload) => {
                      const { dateConfirm, date } = payload || {};
                      dateConfirmField?.input?.onChange(dateConfirm);
                      dateField?.input?.onChange(date);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>
          </Form>
        </div>
      }
    />
  );
};

export default AddMilestoneModal;
